'use strict';

import iban from 'iban';
import {
    isValidPhoneNumber
} from 'libphonenumber-js'

import {
    PropertyValueType,
    propertyValueTypeRegExpressions
} from '~/shared/src/mm/PropertySet';
import LocalizableError from '~/shared/src/base/LocalizableError';


export const validateValueForType = ( value : AttributeValue, type : PropertyValueType, throwError : boolean = false ) : boolean => {
    const placeholders : {
        [ placeholder : string ] : string;
    } = { attribute: 'value' };

    let errorMsg : string = '';

    switch ( type ) {
        case 'BOOLEAN':
            if ( !validateBool( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noBoolean';
            }
            break;

        case 'COLOR':
            if ( !validateColor( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noColor';
            }
            break;

        case 'COORDINATE':
            if ( !validateCoordinate( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noCoordinates';
            }
            break;

        case 'CURRENCY':
        case 'DOUBLE':
        case 'DURATION':
            if ( !validateDouble( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noNumber';
            }
            break;

        case 'DATE':
        case 'TIME':
        case 'TIMESTAMP':
            if ( !validateDateTime( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noDate';
            }
            break;

        case 'EMAIL':
            if ( !validateEmail( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noEmail';
            }
            break;

        case 'IBAN':
            if ( !validateIban( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noIban';
            }
            break;

        case 'INTEGER':
            if ( !validateInteger( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noInteger';
            }
            break;

        case 'JSON':
            if ( !validateJson( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noJson';

                if ( typeof value === 'string' ) {
                    try {
                        JSON.parse( value );
                    }
                    catch ( e ) {
                        if ( e instanceof Error ) {
                            placeholders.errorDetail = e.message;
                        }
                    }
                }
            }
            break;

        case 'LINK':
            if ( !validateLink( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noLink';
            }
            break;

        case 'PHONE':
            if ( !validatePhone( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noPhone';
            }
            break;

        case 'IMAGEDATA':
        case 'STRING':
        case 'TEXT':
        case 'TIMERULE':
        default:
            if ( !validateString( value ) ) {
                errorMsg = 'YN.base.dataObject.common.attribute.validation.noString';
            }
            break;
    }

    if ( throwError && errorMsg !== '' ) {
        const err = new LocalizableError( errorMsg, placeholders );
        err.position = '';
        throw err;
    }

    return errorMsg === '';
}

export const validateBool = ( value : AttributeValue ) : boolean => {
    return typeof value === 'boolean';
}

export const validateColor = ( value : AttributeValue ) : boolean => {
    return typeof value === 'string' && propertyValueTypeRegExpressions.COLOR.test( value );
}

export const validateCoordinate = ( value : AttributeValue ) : boolean => {
    let retVal : boolean = true;

    if (
        !Array.isArray( value )
        || value.length !== 2
        || value[ 0 ] < -90
        || value[ 0 ] > 90
        || value[ 1 ] < -180
        || value[ 1 ] > 180
    ) {
        retVal = false;
    }

    return retVal;
}

export const validateDateTime = ( value : AttributeValue ) : boolean => {
    return Object.prototype.toString.apply( value ) === '[object Date]';
}

export const validateDouble = ( value : AttributeValue ) : boolean => {
    return typeof value === 'number' && !isNaN( value );
}

export const validateEmail = ( value : AttributeValue ) : boolean => {
    return typeof value === 'string' && propertyValueTypeRegExpressions.EMAIL.test( value );
}

export const validateIban = ( value : AttributeValue ) : boolean => {
    let retVal = false;

    if (
        typeof value === 'string'
        && value.length > 12
        && iban.isValid( value )
    ) {
        retVal = true;
    }

    return retVal;
}

export const validateInteger = ( value : AttributeValue ) : boolean => {
    return typeof value === 'number' && value === parseInt( String( value ) );
}

export const validateJson = ( value : AttributeValue ) : boolean => {
    let retVal : boolean = false;

    if ( typeof value === 'string' ) {
        try {
            JSON.parse( value );
            retVal = true;
        }
        catch ( e ) {
        }
    }

    return retVal;
}

export const validateLink = ( value : AttributeValue ) : boolean => {
    return typeof value === 'string' && propertyValueTypeRegExpressions.LINK.test( value );
}

export const validatePhone = ( value : AttributeValue ) : boolean => {
    return typeof value === 'string' && isValidPhoneNumber( value );
}

export const validateString = ( value : AttributeValue ) : boolean => {
    return typeof value === 'string';
}
